import { useState } from 'react';

import { useUpdatePiecesMutation } from '@api/piece';
import {
  Container,
  ContainerRequest,
  Piece,
  PieceAction,
  PieceLocationRequest,
  PieceType,
  Workflow,
} from '@rdTypes/apiContract';
import { getPieceTypeByWorkflow } from 'src/utils/piece';
import { showApiErrorToast } from '@utils/apiErrorToast';
import useLocalStorage from 'src/hooks/useLocalStorage';

export type LocationStateChange = {
  itemSpot?: string | number;
  storageGroupData?: ContainerRequest & Partial<Pick<Container, 'nextOrderNo'>>;
  lockerBankId?: number;
  lockerCell?: string;
  lockerCellSize?: string;
}

export type LocationState = {
  itemType: PieceType;

} & LocationStateChange;

export type LocationLockersError = {
  lockerBankId?: string;
  lockerCell?: string;
};

export const useLocationId = (
  piece: Piece,
  pieceId: number,
  onChange: (piece: Piece) => void,
  onClose: () => void,
  context?: 'capture',
) => {
  const location = piece.pieceLocation;
  const setLastLockerSize = useLocalStorage<string>('rd_last_locker_size', '')[1];

  const [spot, setSpot] = useState<LocationState>({
    itemType: getPieceTypeByWorkflow(piece.workflow),
    itemSpot: location?.packageSpot || '',
    storageGroupData: {} as ContainerRequest,
    lockerBankId: +(location?.container.lockerBankId || 0) || undefined,
    lockerCell: location?.lockerCell || undefined,
    lockerCellSize: location?.lockerCellSize || undefined,
  });

  const onChangeSpot = (params: LocationStateChange) => setSpot({
    itemType: spot.itemType,
    ...params,
  });

  const saveDisabled = piece.workflow === Workflow.Lockers && (!spot.lockerBankId || !(spot.lockerCell || spot.lockerCellSize));

  const [error, setError] = useState<LocationLockersError>({});

  const [updatePieces, { isLoading }] = useUpdatePiecesMutation();

  const request: PieceLocationRequest = {};

  const onSaveLocation = async () => {
    if (piece.workflow === Workflow.Lockers) {
      if (!spot.lockerBankId || !(spot.lockerCell || spot.lockerCellSize)) {
        const errors: LocationLockersError = {};
        if (!spot.lockerBankId) {
          errors.lockerBankId = 'Locker Bank is required';
        }
        if (!spot.lockerCell) {
          errors.lockerCell = 'Locker Cell is required';
        }

        setError(errors);
        return;
      }
      request.lockerBankId = spot.lockerBankId;
      if (spot.lockerCell) {
        request.lockerCell = spot.lockerCell;
      }
      if (spot.lockerCellSize) {
        request.lockerCellSize = spot.lockerCellSize;
      }

    } else {
      if (!spot.storageGroupData) return;
      if (spot.itemType === PieceType.Package) {
        request.packageSpot = spot.itemSpot as string;
      }

      request.container = {
        id: spot.storageGroupData?.id,
        createdDate: spot.storageGroupData?.createdDate,
        containerTypeId: spot.storageGroupData?.containerTypeId,
        containerNumber: spot.storageGroupData?.containerNumber || '',
        sortOrder: spot.storageGroupData?.sortOrder,
      };
      request.orderNo = 0;
    }

    try {
      const result = await updatePieces({
        action: PieceAction.Move,
        pieces: [
          {
            id: pieceId,
            pieceLocation: request,
          },
        ],
        context,
      }).unwrap();

      if (spot.lockerCellSize) {
        setLastLockerSize(spot.lockerCellSize);
      }
      onChange(result[0]);
      onClose();
    } catch (err) {
      showApiErrorToast(err);
    }
  };

  return {
    error,
    spot,
    setSpot: onChangeSpot,
    onSaveLocation,
    isLoading,
    saveDisabled,
    nextOrderNo: spot.storageGroupData?.nextOrderNo,
  };
};
